import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import Button from "../../shared/button/Button.tsx";
import { ConfirmDialogProps } from "../../lib/types/Overlays.ts";
import { useState, useEffect } from "react";
import { XIcon } from "@phosphor-icons/react/dist/icons/X";

const ConfirmDialog = ({
  isOpen,
  onClose,
  onCancel,
  onSubmit,
  dialogClassName,
  title,
  content,
  submitText = "Confirm",
  submitBtnType = "primary",
  closeBtnType = "secondary",
  closeText = "Cancel",
  hideCancel = false,
  confirmDelay = 0,
  showCloseIcon = false,
  commentLabel,
  commentPlaceholder = "Add a comment (optional)",
}: ConfirmDialogProps) => {
  const [confirmEnabled, setConfirmEnabled] = useState(confirmDelay === 0);
  const [timer, setTimer] = useState(confirmDelay);
  const [comment, setComment] = useState("");
  useEffect(() => {
    if (isOpen && confirmDelay > 0) {
      setConfirmEnabled(false);
      setTimer(confirmDelay);
      const interval = setInterval(() => {
        setTimer((prev) => {
          if (prev === 1) {
            clearInterval(interval);
            setConfirmEnabled(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isOpen, confirmDelay]);
  return (
    <Dialog
      open={isOpen}
      onClose={(e) => onClose(e)}
      className="fixed inset-0 z-[999] flex items-center justify-center bg-black/70 p-4"
    >
      <DialogPanel
        className={`relative h-auto w-96 max-w-full overflow-y-auto rounded-xl bg-gray-800 px-10 py-6 shadow-[0_0_24px] shadow-gray-300/40 ${dialogClassName}`}
      >
        {showCloseIcon && (
          <button
            onClick={onCancel}
            className="absolute right-4 top-4 text-gray-300 hover:text-gray-200"
          >
            <XIcon className="h-5 w-5" />
          </button>
        )}
        <div className="mb-4 flex flex-col items-center justify-between gap-y-4">
          {title && (
            <DialogTitle className="text-center text-lg font-normal text-gray-200">
              {title}
            </DialogTitle>
          )}
          {content && (
            <DialogTitle className="text-center text-sm text-gray-300">
              {content}
            </DialogTitle>
          )}
          {commentLabel && (
            <div className="w-full">
              <label className="mb-1 block text-sm text-gray-300">
                {commentLabel}
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={commentPlaceholder}
                rows={3}
                className="w-full resize-none rounded-lg border border-gray-600 bg-gray-700 px-3 py-2 text-sm text-gray-200 placeholder-gray-400 focus:border-app-400 focus:outline-none"
              />
            </div>
          )}
          <div className="mt-4 flex w-full items-center justify-center gap-6">
            <Button
              onClick={() => onSubmit(comment)}
              type="button"
              size="sm"
              variant={submitBtnType}
              disabled={!confirmEnabled}
            >
              {confirmEnabled ? submitText : `Confirm in ${timer}s`}
            </Button>
            {!hideCancel && (
              <Button
                onClick={onCancel}
                type="button"
                size="sm"
                variant={closeBtnType}
              >
                {closeText}
              </Button>
            )}
          </div>
        </div>
      </DialogPanel>
    </Dialog>
  );
};

export default ConfirmDialog;
